/**
 * Application Configuration
 * 
 * This file contains all application-wide configuration settings including:
 * - API endpoints for different environments
 * - Development server ports
 * - Form step definitions
 * - Eligibility rules
 * - Zip code mappings
 * - Data sharing configurations
 * 
 * IMPORTANT: When modifying this file, be aware that changes can affect:
 * - Form flow and step navigation
 * - Eligibility checks and ineligibility messages
 * - API communication
 * - Field clearing behavior
 * 
 * @module config
 */

import steps from '@src/steps'

export default {
  /**
   * API Base URLs
   * Maps hostnames to their corresponding API base URLs
   * Used by getApiURL utility to determine the correct API endpoint
   * 
   * @type {Object.<string, string>}
   */
  apiUrl: {
    'localhost:8000': 'http://127.0.0.1',
    'localhost:3000': 'http://127.0.0.1',
    'host.homeworksenergy.com': 'https://hostapi.homeworksenergy.com',
    'hosttest.homeworksenergy.com': 'https://hosttestapi.homeworksenergy.com',
  },

  /**
   * Development Server Ports
   * Port numbers for local development API services
   * These ports are used when running the application locally
   * 
   * @type {Object.<string, string>}
   */
  devPort: {
    heaSlots: '3001',        // Home Energy Assessment appointment slots
    heaSubmit: '3002',       // Home Energy Assessment form submission
    zipCodeValid: '3003',    // Zip code validation service
    hvacOpenings: '3004',    // HVAC appointment openings
    hvacServiceSubmit: '3005', // HVAC service form submission
    empRefCode: '3006',      // Employee referral code validation
    hvacSalesSubmit: '3007', // HVAC sales form submission
  },

  /**
   * Form Steps Configuration
   * Defines the order and components for each step in the multi-step form
   * The order of these steps determines the user's journey through the application
   * 
   * Step flow:
   * 1. ChoosePath - User selects HEA or HVAC service (skipped when entering via /HealthyHome)
   * 2. Eligibility - User answers eligibility questions (HEA / HVAC / HealthyHome variants)
   * 3. ContactInfo - User provides contact information
   * 4. Schedule - User selects appointment date/time (skipped for some paths)
   * 5. Success - Confirmation page after submission
   *
   * NOTE: Steps can be skipped dynamically based on user selections
   * (e.g., Schedule step is skipped for HVAC Service, CAP HEA, and paused-gas HEA
   * lead customers — Eversource and Columbia Gas; see src/utils/isEversourceGasLead.js)
   * 
   * @type {Array<React.Component>}
   */
  steps: [
    steps.ChoosePath,
    steps.Eligibility,
    steps.ContactInfo,
    steps.Schedule,
    steps.Success,
  ],

  /**
   * Tetra Partner URL
   * Redirect URL for customers outside HomeWorks Energy service area
   * Used when customer's location is not serviced by HomeWorks Energy
   * 
   * @type {string}
   */
  tetraUrl:
    'https://www.tetra.com/services/home-page-signup-form?utm_source=HWE&utm_medium=HWE',

  /**
   * Field Clearing Exemptions
   * 
   * When a user changes an earlier form value, the application automatically
   * clears dependent fields that follow. This array lists field names that
   * should NEVER be cleared, even when earlier values change.
   * 
   * Exempt fields:
   * - 'contactInfo': Preserves contact information for better UX
   * - 'step': Current step identifier (needed for navigation)
   * - 'path': Selected service path (HEA or HVAC) - fundamental to the flow
   * 
   * See: src/utils/clearFollowingValues.js for implementation
   * 
   * @type {Array<string>}
   */
  clearFollowingValuesExempt: ['contactInfo', 'step', 'path'],

  /**
   * Eligibility Rules
   * 
   * Defines conditions that make a customer ineligible for services.
   * When a condition matches, the user sees an ineligible modal and cannot proceed.
   * 
   * Rule Structure:
   * - 'when': Field name(s) to check (string for single field, array for multiple)
   * - 'is': Expected value(s) (string for exact match, function for complex logic)
   * - 'message': Error message to display (string or function that returns string)
   * 
   * Rule Types:
   * 1. Simple match: { when: 'fieldName', is: 'expectedValue', message: '...' }
   * 2. Multiple fields: { when: ['field1', 'field2'], is: (val1, val2) => boolean, message: '...' }
   * 3. Dynamic message: { when: 'fieldName', is: 'value', message: () => 'dynamic message' }
   * 
   * IMPORTANT: The order of rules matters - first matching rule is used.
   * Rules are evaluated by isIneligibleTest utility (src/utils/isIneligibleTest.js)
   * 
   * @type {Array<Object>}
   */
  // User is ineligible when one of the following matches
  ineligibleValues: [
    /**
     * Rule: Cannot service area
     * Customer's location is not serviced by HomeWorks Energy for HVAC sales
     */
    {
      when: 'canService', // Field name from form values
      is: 'no', // Expected value that triggers ineligibility
      message:
        'Unfortunately we do not service your area for heating and cooling sales visits at this time.',
    },
    /**
     * Rule: Renter (not homeowner)
     * Only homeowners can use the online scheduler; renters must call
     */
    {
      when: 'rentOrOwn',
      is: 'rent',
      message:
        'HomeWorks Energy would love to help you with your needs. Our online scheduler only supports homeowners at this time. \n\n Please call (781) 305-3319 ext. 8 to schedule an appointment.',
    },
    /**
     * Rule: Received assessment in last 2 years
     * Mass Save assessments can only be done once every 2 years
     */
    {
      when: ['twoYears', 'path'],
      is: (twoYears, path) => twoYears === 'yes' && path === 'hea',
      message:
        'Sorry, your home only qualifies for a Mass Save Energy Assessment once every two years.\n\nFor more information, please call a HomeWorks Energy Specialist at (781) 305-3319.',
    },
    /**
     * Rule: 5+ Multi-Family for HEA
     * Multi-family buildings with 5+ units cannot use online scheduler for HEA
     * Uses function to check multiple conditions
     */
    {
      when: ['singleOrMulti', 'path'], // Multiple fields to check
      is: (singleOrMulti, path) => // Function receives values in same order as 'when' array
        singleOrMulti === '5+ Multi-Family' && (path === 'hea' || path === 'healthyhome'),
      message:
        'HomeWorks Energy currently only serves single family and multi-family homes of 2-4 units. To access Mass Save program benefits for larger multi-family buildings please see the information on the Mass Save website below: https://www.masssave.com/en/saving/energy-assessments/multi-family-facilities-5-units-plus',
    },
    /**
     * Rule: 5+ Family Size for HVAC
     * HVAC sales appointments only support 1-4 unit buildings
     */
    {
      when: ['familySize', 'path'],
      is: (familySize, path) => ['5+'].includes(familySize) && path === 'hvac',
      message:
        'HomeWorks Energy would love the opportunity to serve your multi-family home for a sales appointment. Our online scheduler currently supports only Single-Family and multi-family homes of 1-4 units.\n\nPlease call (781) 305-3319 ext. 2 to speak with a representative.',
    },
    {
      when: ['howHeated', 'electricProvider'],
      is: (howHeated, electricProvider) =>
        howHeated !== 'Gas' && electricProvider === 'Municipal',
      message:
        'Sorry, Mass Save does not provide benefits to customers whose primary utility is not a program sponsor.\n\nFor more information, please call a HomeWorks Energy Specialist at (781) 305-3319.',
    },
    {
      when: ['homeAge'],
      is: (homeAge) => homeAge === 'new',
      message: 'Sorry, we do not service new constructions',
    },
    {
      when: ['gasProvider', 'electricProvider'],
      is: (gasProvider, electricProvider) =>
        (gasProvider === 'Berkshire Gas' ||
          electricProvider === 'Berkshire Gas'),
      message:
        'Berkshire Gas customers, please call us at 800-944-3212 to arrange your home energy assessment',
    },
    {
      when: ['fuelAssistance', 'salesOrService', 'path'],
      is: (fuelAssistance, salesOrService, path) =>
        fuelAssistance === 'yes' &&
        salesOrService === 'sales' &&
        path === 'hvac',
      message: () =>
        `Based on your utility discount rate, we recommend starting with a Mass Save Home Energy Assessment to help you qualify for additional savings and rebates.\n\nClick here to schedule your Energy Assessment: <a href="/choosePath" class="notice-link">Schedule Mass Save HEA</a>\n\nOr call (781) 305-3319 for assistance.`,
    },
  ],

  /**
   * Data Sharing Authorization Towns
   * 
   * List of towns/cities where residents can authorize data sharing
   * with local government (e.g., Wellesley sustainability staff)
   * 
   * When a customer's address matches one of these towns, they see
   * an optional checkbox to authorize data sharing.
   * 
   * Used in: src/steps/ContactInfo/ContactInfo.jsx
   * 
   * @type {Array<string>}
   */
  // prettier-ignore
  dataSharingTowns : ['Wellesley'],

  /**
   * Columbia Gas Zip Code Mappings
   * 
   * IMPORTANT BUSINESS LOGIC:
   * After Eversource acquired Columbia Gas, customers entering their
   * gas provider as "Eversource" may actually be in Columbia Gas service
   * areas. This mapping ensures the scheduler fetches correct appointment
   * slots based on zip code, regardless of what the customer selected.
   * 
   * The application checks customer zip codes against these lists and
   * overrides the program selection to 'ColumbiaGas' or 'CapeLight' accordingly.
   * 
   * Used in: src/utils/getOpenings.js
   * 
   * @type {Object}
   * @property {Array<string>} otherRegions - Zip codes for Columbia Gas (non-Cape Cod regions)
   * @property {Array<string>} capeCod - Zip codes for Cape Light Compact (Cape Cod region)
   */
  columbiaGasZipCodes: {
    otherRegions: [
      '01001',
      '01013',
      '01014',
      '01020',
      '01021',
      '01022',
      '01027',
      '01028',
      '01033',
      '01057',
      '01060',
      '01061',
      '01063',
      '01069',
      '01075',
      '01077',
      '01089',
      '01090',
      '01101',
      '01102',
      '01103',
      '01104',
      '01105',
      '01106',
      '01107',
      '01108',
      '01109',
      '01111',
      '01115',
      '01116',
      '01118',
      '01119',
      '01128',
      '01129',
      '01138',
      '01139',
      '01144',
      '01152',
      '01199',
      '01756',
      '01810',
      '01840',
      '01841',
      '01842',
      '01843',
      '01844',
      '01845',
      '02019',
      '02021',
      '02032',
      '02035',
      '02038',
      '02048',
      '02050',
      '02051',
      '02052',
      '02053',
      '02056',
      '02059',
      '02060',
      '02061',
      '02066',
      '02067',
      '02071',
      '02072',
      '02081',
      '02093',
      '02301',
      '02302',
      '02303',
      '02304',
      '02305',
      '02322',
      '02324',
      '02325',
      '02331',
      '02332',
      '02333',
      '02334',
      '02338',
      '02339',
      '02340',
      '02341',
      '02343',
      '02347',
      '02348',
      '02356',
      '02357',
      '02358',
      '02359',
      '02368',
      '02375',
      '02379',
      '02703',
      '02715',
      '02760',
      '02761',
      '02763',
      '02764',
      '02766',
      '02767',
      '02768',
      '02769',
      '02771',
      '02779',
      '02780',
      '02783',
      '05501',
      '05544',
    ],
    capeCod: [
      '02537',
      '02542',
      '02561',
      '02562',
      '02563',
      '02536',
      '02540',
      '02541',
      '02543',
      '02556',
      '02565',
      '02574',
      '02534',
      '02553',
      '02559',
      '02532',
      '02630',
      '02664',
      '02631',
      '02632',
      '02633',
      '02635',
      '02635',
      '02636',
      '02637',
      '02638',
      '02639',
      '02641',
      '02643',
      '02642',
      '02644',
      '02645',
      '02646',
      '02601',
      '02647',
      '02649',
      '02649',
      '02650',
      '02651',
      '02652',
      '02653',
      '02657',
      '02659',
      '02660',
      '02661',
      '02662',
      '02663',
      '02664',
      '02666',
      '02667',
      '02668',
      '02669',
      '02670',
      '02671',
      '02672',
      '02673',
      '02675',
    ],
  },
}
